/**
 * Client-generated ids. Framework-free (copied to next-mapit).
 *
 * No `uuid` dependency: it is only transitive in this repo.
 */

const HEX = Array.from({ length: 256 }, (_, byte) =>
  byte.toString(16).padStart(2, "0"),
);

const formatV4 = (bytes) => {
  // Force the version (4) and RFC 4122 variant (10xx) bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => HEX[byte]);
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
};

/**
 * A UUID v4 for `POST /chat` `clientMessageId` (the server dedupes on it → 858).
 *
 * Uses `crypto.randomUUID` (secure contexts only), then
 * `crypto.getRandomValues` (also on plain-http LAN dev URLs), then
 * `Math.random`. Every path yields a valid v4.
 *
 * @param {Pick<Crypto, "randomUUID" | "getRandomValues"> | null} [cryptoImpl]
 *   Defaults to the global `crypto`; pass `null` to force the `Math.random` path (tests).
 * @returns {string}
 */
export function newClientMessageId(
  cryptoImpl = typeof crypto === "undefined" ? null : crypto,
) {
  if (typeof cryptoImpl?.randomUUID === "function") {
    return cryptoImpl.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof cryptoImpl?.getRandomValues === "function") {
    cryptoImpl.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return formatV4(bytes);
}

/**
 * A local id for a message that has no server id yet (replaced on `meta`).
 * Never collides with server ids (Mongo ObjectIds).
 *
 * @param {string} [prefix]
 * @returns {string} e.g. `tmp-user-3f6c…`
 */
export const newTempId = (prefix = "tmp") => `${prefix}-${newClientMessageId()}`;
